import React, { useRef, useState } from 'react';
import { Card, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "../../components/BasicLayout";

// Password utility for validation schema
import PasswordUtility from '../../../passwordutility';

// Images
import bgImage from "assets/images/loginbackground.jpg";
import brandWhite from "assets/images/login_page_logo.png";
import mastercardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import pciLogo from "assets/images/pcilogo.png";
import botimLogo from "assets/images/botim.png";
import version from "../../../../version";
import useApi from "../../../../components/Utils/api";
import { useTranslation } from "react-i18next";

function ResetWithOtp() {
  const api = useApi();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  // State for the 4 individual OTP digit boxes
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const otpInputRefs = useRef([]);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  // Use the validation schema from PasswordUtility
  const { register, handleSubmit, reset, formState: { errors }, trigger, watch, control, setValue } = useForm({
    mode: "onChange",
    resolver: yupResolver(PasswordUtility.getPasswordValidationSchema()), // Use schema from PasswordUtility
    defaultValues: {
      username: state?.username || "",
      password: "",
      otp: "",
    },
  });

  const handleOtpChange = (index, e) => {
    const digit = e.target.value.replace(/[^0-9]/g, "").slice(-1);
    const nextDigits = [...otpDigits];
    nextDigits[index] = digit;
    setOtpDigits(nextDigits);
    setValue("otp", nextDigits.join(""), { shouldValidate: true });
    if (digit && index < otpInputRefs.current.length - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      const nextDigits = [...otpDigits];
      nextDigits[index - 1] = "";
      setOtpDigits(nextDigits);
      setValue("otp", nextDigits.join(""), { shouldValidate: true });
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedDigits = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 4).split("");
    if (pastedDigits.length === 0) return;
    const nextDigits = ["", "", "", ""];
    pastedDigits.forEach((digit, i) => (nextDigits[i] = digit));
    setOtpDigits(nextDigits);
    setValue("otp", nextDigits.join(""), { shouldValidate: true });
    otpInputRefs.current[Math.min(pastedDigits.length, 3)]?.focus();
  };

  // Submit form handler
  const formSubmit = () => {
    handleSubmit(formHandler)();
  }

  // Handle form submission to the API
  const formHandler = (form) => {
    if (state === null) {
      toast.error("Session Expired! Please initiate a reset request first.");
      navigate("/forgot-password");
      return;
    }
    const data = {
      username: form.username,
      password: form.password,
      otp: form.otp,
    }

    // API call to reset password
    api.post("auth/admin/reset-password", data)
      .then(response => {
        if (response.data.statusCodeValue === 200) {
          toast.success(response.data.body.message);
          navigate("/login");
        } else {
          if (response.data.body.message) {
            toast.error(response.data.body.message, { id: "RESET_PASSWORD_ERROR" });
          }
        }
      })
      .catch(error => {
        if (error.response.data.body.message) {
          toast.error(error.response.data.body.message)
        }
      })
  }

  return (
    <BasicLayout image={bgImage} hideFooter>
      <MDBox display="flex" alignItems="center" justifyContent="center" mb={4}>
        <MDBox component="img" src={brandWhite} alt="Youcloud" sx={{ height: "128px" }} />
      </MDBox>
      <Card sx={{ minHeight: "370px", display: "flex", flexDirection: "column" }}>
        <MDBox pt={4} pb={1} px={3} textAlign="left">
          <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ letterSpacing: "0.5px" }}>
            {t('reset_password')}
          </MDTypography>
          {/* <MDTypography display="block" variant="button" color="text" fontWeight="regular" mt={1}>
            You will receive email on your registered email address.
          </MDTypography> */}
        </MDBox>
        <MDBox
          pt={1}
          pb={3}
          px={3}
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <FormProvider {...{ register, handleSubmit, reset, formState: { errors }, trigger, watch, control }}>
            <MDBox sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label={t('username')}
                    name="username"
                    error={Boolean(errors.username)} // Retain error state styling (optional)
                    helperText={errors.username ? errors.username.message : ''} // Show error message if there is an error
                    fullWidth
                    {...register("username")}
                    disabled
                    focused
                  />
                </MDBox>
                <MDBox mb={2} style={{ position: 'relative' }}>
                  {/* Password Input Field with Eye Icon and Info Icon on Right */}
                  <MDInput
                    type={showPassword ? "text" : "password"} // Toggle between text and password
                    label={t('new_password')}
                    name="password"
                    error={Boolean(errors.password)} // Enable error state styling for password field
                    helperText={errors.password ? errors.password.message : ''} // Show error message below the field
                    fullWidth
                    {...register("password")}
                    required
                    InputProps={{
                      style: { position: 'relative', color: 'inherit' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            style={{
                              background: 'transparent', // Remove background color
                              borderRadius: '0', // Remove border radius
                              boxShadow: 'none', // Remove box shadow
                              padding: 0,
                              marginRight: '10px', // Add margin to separate icons
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="caption" fontWeight="regular" color="text" mb={0.5} display="block">
                    OTP
                  </MDTypography>
                  <MDBox display="flex" justifyContent="center" gap={1.5}>
                    {otpDigits.map((digit, index) => (
                      <MDInput
                        key={index}
                        type="password"
                        inputRef={(el) => (otpInputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        error={Boolean(errors.otp)}
                        inputProps={{
                          maxLength: 1,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          style: { textAlign: "center", fontSize: "1rem", fontWeight: 600 },
                        }}
                        sx={{
                          width: "44px",
                          "& .MuiOutlinedInput-input": { padding: "8px 0" },
                          backgroundImage: "none !important",
                        }}
                      />
                    ))}
                  </MDBox>
                  {errors.otp && (
                    <MDTypography variant="caption" color="error" mt={0.5} display="block" textAlign="center">
                      {errors.otp.message}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
              <MDBox mb={1}>
                <MDButton onClick={() => formSubmit()} variant="gradient" color="info" fullWidth>
                  {t('reset')}
                </MDButton>
              </MDBox>
            </MDBox>
          </FormProvider>
        </MDBox>
      </Card>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={3} mt={3}>
        <MDBox component="img" src={mastercardLogo} alt="Mastercard" sx={{ height: "28px" }} />
        <MDBox component="img" src={visaLogo} alt="Visa" sx={{ height: "28px" }} />
        <MDBox component="img" src={pciLogo} alt="PCI DSS Compliant" sx={{ height: "28px" }} />
        <MDBox component="img" src={botimLogo} alt="Botim" sx={{ height: "28px" }} />
      </MDBox>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} mt={1.5} sx={{ whiteSpace: "nowrap" }}>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          &copy; {new Date().getFullYear()} YouCloud Technologies. All Rights Reserved.
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          {t('version')} {version}
        </MDTypography>
      </MDBox>
    </BasicLayout>
  );
}

export default ResetWithOtp;
